import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import { ThemeToggle } from '../ThemeToggle';
import { Button } from '../ui/button';
import { SignOutConfirmationModal } from '../SignOutConfirmationModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Search, User, Settings, LogOut, Menu } from 'lucide-react';
import { Input } from '../ui/input';
import { getAvatarPreset } from '../../utils/avatarPresets';

interface HeaderProps {
  onMenuClick?: () => void;
  isInteractionBlocked?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick, isInteractionBlocked = false }) => {
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const [headerQuery, setHeaderQuery] = useState('');
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const actionsRef = useRef<HTMLDivElement>(null);
  const avatarPreset = getAvatarPreset(user?.avatarUrl || user?.avatar);
  const avatarFallback = user?.name ? user.name.substring(0, 2).toUpperCase() : "US";

  useEffect(() => {
    if (!actionsRef.current) return;
    if (isInteractionBlocked) {
      actionsRef.current.setAttribute('inert', '');
    } else {
      actionsRef.current.removeAttribute('inert');
    }
  }, [isInteractionBlocked]);

  const handleConfirmLogout = async () => {
    setIsSigningOut(true);
    try {
      await logout();
      setShowSignOutModal(false);
      navigate('/welcome', { replace: true });
    } finally {
      setIsSigningOut(false);
    }
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = headerQuery.trim();

    if (!query) {
      navigate('/history');
      return;
    }

    const params = new URLSearchParams();
    params.set('q', query);
    navigate({
      pathname: '/history',
      search: `?${params.toString()}`,
    });
  };

  return (
    <>
      <header
        className="sticky top-0 flex h-[calc(4rem+env(safe-area-inset-top))] w-full items-center gap-4 border-b bg-background/95 px-4 pt-[env(safe-area-inset-top)] sm:px-6 backdrop-blur supports-[backdrop-filter]:bg-background/60"
        style={{ zIndex: 'var(--z-header)' }}
      >
        <div className="flex flex-1 items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={onMenuClick}
            aria-label="Open navigation menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
          <form
            className="hidden w-full max-w-sm sm:flex"
            onSubmit={handleSearchSubmit}
            data-testid="global-search-form"
          >
            <div className="relative w-full">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search essays, topics..."
                className="w-full pl-9 md:w-[300px] lg:w-[300px]"
                aria-label="Search attempts"
                data-testid="global-search-input"
                value={headerQuery}
                onChange={(event) => setHeaderQuery(event.target.value)}
              />
            </div>
          </form>
        </div>
        <div
          ref={actionsRef}
          className={isInteractionBlocked ? "flex items-center gap-4 pointer-events-none" : "flex items-center gap-4"}
          aria-hidden={isInteractionBlocked}
        >
          <ThemeToggle />
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="relative h-9 w-9 rounded-full" aria-label="Open account menu">
                <Avatar className="h-9 w-9">
                  {!avatarPreset && <AvatarImage src={user?.avatar || user?.avatarUrl} alt={user?.name || "User"} />}
                  <AvatarFallback
                    style={avatarPreset ? { backgroundColor: avatarPreset.color, color: '#ffffff' } : undefined}
                  >
                    {avatarPreset ? avatarPreset.emoji : avatarFallback}
                  </AvatarFallback>
                </Avatar>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-56" align="end" forceMount>
              <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">{user?.name || "Guest User"}</p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email || "guest@example.com"}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                <span>Profile</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="mr-2 h-4 w-4" />
                <span>Settings</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setShowSignOutModal(true)}
                className="text-red-600 dark:text-red-400"
                data-testid="header-signout-menu-item"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Sign Out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <SignOutConfirmationModal
        isOpen={showSignOutModal}
        isLoading={isSigningOut}
        onClose={() => setShowSignOutModal(false)}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
};
